import { FC, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { HubError } from "../../class/Error";
import { showNotification } from "../../store/slices/notificationSlice";
import { SkeletonBotScreen } from "./components/SkeletonBotScreen/SkeletonBotScreen";
import BotScreen from "./components/BotScreen";
import Header from "../../_modules/Header/Header";
import PageLayout from "../../_components/PageLayout";
import { useApi } from "../../_hooks/use-api";
import { AutoStrategyInfo, FuturesBotInfo } from "./types";
import { StrategyTypeResponse } from "../../_app/types";
import { useTranslation } from "react-i18next";

const INITIAL_FUTURES_BOTS = {
  currency: "USDT",
  futuresGrid: {
    archivedTotal: 0,
    activeTotal: 0,
    name: "FUTURES_GRID",
  },
  spotGrid: {
    archivedTotal: 0,
    activeTotal: 0,
    name: "SPOT_GRID",
  },
  gridPnl: 0,
  realizedPnl: 0,
  totalProfit: 0,
};

const INITIAL_AUTO_STRATEGY = {
  totalProfit: 0,
  strategies: [
    {
      archived: 0,
      active: 0,
      name: "AUTO_STRATEGIES",
    },
  ],
};

export const BotCategoriesPage: FC = () => {
  const api = useApi();
  const { t } = useTranslation("bots");

  const user = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();

  const [futuresBots, setFuturesBots] = useState<FuturesBotInfo>(INITIAL_FUTURES_BOTS);
  const [isLoadingFuturesBots, setIsLoadingFuturesBots] = useState<boolean>(false);

  const [autoStrategies, setAutoStrategies] = useState<AutoStrategyInfo>(INITIAL_AUTO_STRATEGY);
  const [isLoadingAutoStrategies, setIsLoadingAutoStrategies] = useState<boolean>(false);

  async function getFuturesBots(profileId: string, accountId: string) {
    setIsLoadingFuturesBots(true);

    try {
      const response: FuturesBotInfo = await api.botTypes(profileId, accountId);

      if (response.futuresGrid || response.spotGrid) {
        setFuturesBots(response);
      } else {
        setFuturesBots(INITIAL_FUTURES_BOTS);
      }
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.errorCode} ${error.message}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    }

    setIsLoadingFuturesBots(false);
  }

  async function getAutoStrategies(profileId: string, accountId: string) {
    setIsLoadingAutoStrategies(true);
    try {
      const response: StrategyTypeResponse = await api.strategyTypes(profileId, accountId);

      setAutoStrategies(response);
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.errorCode} ${error.message}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    }

    setIsLoadingAutoStrategies(false);
  }

  useLayoutEffect(() => {
    if (!user.profile.profileId || !user.activeAccount.id) return;

    getAutoStrategies(user.profile.profileId, user.activeAccount.id);
    getFuturesBots(user.profile.profileId, user.activeAccount.id);
  }, [user.activeAccount.id, user.profile.profileId]);

  return (
    <PageLayout contentSx={{ padding: "20px 8px 40px" }} header={<Header pageTitle={t("bots")} />}>
      {isLoadingFuturesBots || isLoadingAutoStrategies ? (
        <SkeletonBotScreen />
      ) : (
        <BotScreen futuresBots={futuresBots} autoStrategies={autoStrategies} />
      )}
    </PageLayout>
  );
};
